import React, { useState } from "react";

import moment from "moment";

import { Property } from "manager/interfaces/api";
import { DatePicker, TextArea } from "shared/components/Form";
import { Modal } from "shared/components/Modal";
import useModal from "shared/hooks/useModal";

import {
  formatBillingDate,
  isDateOnOrBeforeToday,
  todayDateInput,
  toBillingDateTime,
  toBillingWriteAt,
} from "./billingDisplay";
import {
  PropertySummary,
  PropertySummaryName,
  PropertySummaryUnits,
  RestartHint,
  SetLiveBody,
  WarningNote,
} from "./styled";

interface CorrectGoLiveModalProps {
  property: Property;
  submit: (payload: {
    billingLiveAt: string;
    reason: string;
  }) => Promise<void> | void;
  submitting?: boolean;
}

const CorrectGoLiveModal = ({
  property,
  submit,
  submitting = false,
}: CorrectGoLiveModalProps) => {
  const { closeActiveModalDialog } = useModal();
  const current = property.billingLiveAt
    ? moment(property.billingLiveAt).format("YYYY-MM-DD")
    : todayDateInput();
  const [date, setDate] = useState(current);
  const [reason, setReason] = useState("");
  const changed = Boolean(
    date && date !== current && isDateOnOrBeforeToday(date)
  );
  const canSubmit = changed && Boolean(reason.trim());

  return (
    <Modal
      title="Correct go-live date"
      subtitle="This changes when billing counts from. Finance will be notified in Slack."
      submit={() =>
        submit({
          billingLiveAt: toBillingWriteAt(date),
          reason: reason.trim(),
        })
      }
      submitButtonLabel="Save correction"
      submitButtonDisabled={!canSubmit}
      submitting={submitting}
      closeOnSubmit={false}
      afterClose={closeActiveModalDialog}
      showCancelLink
      width={560}
    >
      <Modal.Body>
        <SetLiveBody>
          <PropertySummary>
            <PropertySummaryName>{property.name}</PropertySummaryName>
            <PropertySummaryUnits>
              Currently live since {formatBillingDate(property.billingLiveAt)}
            </PropertySummaryUnits>
          </PropertySummary>
          <div>
            <DatePicker
              id="billingLiveAt"
              name="billingLiveAt"
              label="Correct go-live date"
              value={date}
              required
              onChange={setDate}
              disabledDate={(currentDay) => currentDay.isAfter(moment(), "day")}
            />
            <RestartHint>
              Today or a past date only. You cannot move this into the future.
            </RestartHint>
          </div>
          <TextArea
            id="reason"
            name="reason"
            label="Reason (required)"
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {changed && (
            <WarningNote>
              Billing will count from{" "}
              {formatBillingDate(toBillingDateTime(date))} instead of{" "}
              {formatBillingDate(property.billingLiveAt)}.
            </WarningNote>
          )}
        </SetLiveBody>
      </Modal.Body>
    </Modal>
  );
};

export default CorrectGoLiveModal;
